import React from 'react';
import { useLocation } from "react-router-dom";
import { Button, Flex, Heading, Icon, Select, Tooltip, useDisclosure } from '@chakra-ui/react';
import { FaBook, FaGlobe } from 'react-icons/fa';
import { RiQuestionnaireLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import { ApplicationPathName } from '../contexts/constants';
import { supportedLngs } from '../utils/i18n';
import ManualModal from './ManualModal';

/**
 * アプリケーションヘッダー部
 * @param props
 * @returns
 */
const AppHeader: React.FC = (props) => {
  const { t, i18n } = useTranslation();

  /** 現在のURL */
  const pathname: String = useLocation().pathname.toLocaleLowerCase();

  /**
   * 言語切替ボタンの表示可否。
   * ホーム画面（'/home'）のみに表示する。
   * ログイン直後のデフォルト遷移先やcatch-allルートで表示される '/' もホーム扱いとする。
   */
  const isHome = pathname === ApplicationPathName.Home.Url || pathname === '/';

  /** マニュアルモーダル */
  const { isOpen: isManualModalOpen, onOpen: onManualModalOpen, onClose: onManualModalClose } = useDisclosure();

  /**
   * 表示言語を切り替える（日本語 / 英語）。
   * プルダウン（supportedLngs）に言語を追加すれば選択肢は自動的に増える。
   * 選択結果は i18next-browser-languagedetector により localStorage（キー: i18nextLng）へ自動保存される。
   */
  const handleChangeLanguage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(e.target.value);
  };

  /**
   * 画面タイトルを取得する。
   * @returns 画面タイトル
   */
  const getTitle = () => {
    switch (pathname) {
      // ホーム
      case ApplicationPathName.Home.Url: return t('features.Home.title');

      // 投資
      case ApplicationPathName.InputList.Url: return t('features.Input.List.title');
      case ApplicationPathName.ApproveList.Url: return t('features.Approve.List.title');
      case ApplicationPathName.InvestmentList.Url: return t('features.Investment.List.title');
      case ApplicationPathName.ManagementList.Url: return t('features.Management.List.title');
      case ApplicationPathName.InvestmentScreeningDetail.Url: return t('features.Investment.ScreeningDetail.title');
      // 状況報告（検収管理） ※MOC段階のためi18n未対応。本実装時にt('features.StatusReport.title')へ切り替える。
      case ApplicationPathName.StatusReport.Url: return ApplicationPathName.StatusReport.Title;

      // マスタメンテナンス
      case ApplicationPathName.EquipmentCategory.Url: return t('features.MasterMaintenance.EquipmentCategory.title');
      case ApplicationPathName.EquipmentGroup.Url: return t('features.MasterMaintenance.EquipmentGroup.title');
      case ApplicationPathName.EquipmentSupplier.Url: return t('features.MasterMaintenance.EquipmentSupplier.title');
      case ApplicationPathName.InterestRateMaster.Url: return t('features.MasterMaintenance.InterestRateMaster.title');
      case ApplicationPathName.CompanyPlant.Url: return t('features.MasterMaintenance.CompanyPlant.title');
      case ApplicationPathName.Nationality.Url: return t('features.MasterMaintenance.Nationality.title');
      case ApplicationPathName.Notification.Url: return t('features.MasterMaintenance.Notification.title');
      case ApplicationPathName.AccountingOrganization.Url: return t('features.MasterMaintenance.AccountingOrganization.title');
      case ApplicationPathName.SystemAccessSetting.Url: return t('features.MasterMaintenance.SystemAccessSetting.title');
      case ApplicationPathName.ApprovalRequesterMaster.Url: return t('features.MasterMaintenance.ApprovalRequesterMaster.title');
      case ApplicationPathName.AIRestrictedOrganization.Url: return t('features.MasterMaintenance.AIRestrictedOrganization.title');
      case ApplicationPathName.ActiveUsers.Url: return t('features.MasterMaintenance.ActiveUsers.title');
      case ApplicationPathName.InquireSetting.Url: return t('features.MasterMaintenance.InquireSetting.title');
      case ApplicationPathName.CheckListMaster.Url: return t('features.MasterMaintenance.CheckListMaster.title');

      default: return t('features.Home.title');
    }
  }

  /**
   * FAQアイコン押下時の処理
   * 別タブで開き、既存のFAQタブのある時はそのタブをアクティブにする
   */
  const handleOpenFAQ = () => {
    const faqWindow = window.open(ApplicationPathName.Faq.Url, 'FAQ');
    // 既存でFAQタブのある時、そのタブをフォーカスする
    if (faqWindow) {
      faqWindow.focus();
    }
  };

  return (
    <>
      <Flex marginLeft={5} marginTop={5}>
        <Heading size="lg">{getTitle()}</Heading>
      </Flex>
      {/* 言語切替プルダウン（FAQボタンの左に配置／ホーム画面のみ表示） */}
      {isHome && (
        <Tooltip label={t('components.AppHeader.tooltip.language')}>
          <Flex
            position="fixed"
            top="16px"
            right="136px"
            zIndex="10"
            alignItems="center"
            bg="white"
            borderRadius="md"
          >
            <Icon as={FaGlobe} color="#2B6CB0" marginLeft={2} marginRight={1} />
            <Select
              aria-label={t('components.AppHeader.tooltip.language')}
              h="30px"
              w="110px"
              fontSize="sm"
              variant="ghost"
              value={i18n.language}
              onChange={handleChangeLanguage}
            >
              {Object.entries(supportedLngs).map(([lng, label]) => (
                <option key={lng} value={lng}>{label}</option>
              ))}
            </Select>
          </Flex>
        </Tooltip>
      )}
      {/* マニュアルボタン（メニューボタンの左に配置） */}
      <Tooltip label={t('components.AppHeader.tooltip.manual')} placement="bottom-end">
        <Button
          h="30px"
          w="30px"
          fontSize="xl"
          variant="ghost"
          onClick={onManualModalOpen}
          position="fixed"
          top="16px"
          right="56px"
          zIndex="10"
        >
          <Icon as={FaBook} color="#2B6CB0" />
        </Button>
      </Tooltip>
      {/* FAQボタン（マニュアルボタンの左に配置） */}
      <Tooltip label={t('features.faq.label.openFaqInNewTab')}>
        <Button
          h="30px"
          w="30px"
          fontSize="xl"
          variant="ghost"
          onClick={handleOpenFAQ}
          position="fixed"
          top="16px"
          right="96px"
          zIndex="10"
        >
          <Icon as={RiQuestionnaireLine} color="#2B6CB0" />
        </Button>
      </Tooltip>
      <ManualModal isOpen={isManualModalOpen} onClose={onManualModalClose} />
    </>
  );
};

export default AppHeader;
